import { reactive, toValue, watch, type ComputedRef, type MaybeRefOrGetter, type Ref } from "vue";
import { useRoute, useRouter, type Router } from "vue-router";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";

dayjs.extend(customParseFormat);

/**
 * Declarative UI filter fields, owned by the query.
 *
 * A page used to hold three things per filter: a local ref (or a hand-rolled writable
 * computed mapping UI value ↔ API shape), a `queryKey` on the input component, and — for
 * ranges — a `useDateRangeFilter` call writing bounds into `query.filters`. That put two
 * writers on the same state (the component's URL watcher and the page's filter watcher),
 * which is what forced the echo guards in the old `FilterSelect` / `FilterDateRange`.
 *
 * Here a field is declared once, next to the query, and owns all three jobs:
 *
 * 1. **URL sync** — the field reads the route on mount (deep link wins) and writes back on
 *    change, so a pasted URL reproduces the sender's exact view.
 * 2. **Request filters** — the resolved value is written into the query's `filters` ref
 *    under the API field name(s).
 * 3. **The v-model** — `query.f.<name>` binds straight to a dumb input component.
 *
 * Because the field is the single writer for its own URL params, the inputs need no
 * `queryKey` and no reconciliation logic of their own.
 *
 * A declaration is data, not behaviour: `{ type, field, initial }` and — only where the
 * mapping is genuinely custom — an `encode` / `parse` override. All the wiring lives in
 * `useFilterFields`, which holds every model in ONE `reactive` bag and runs ONE watcher over
 * it. A single writer for the whole bag is what removes the per-field echo guards: fields
 * that change together (a reset, a tab switch, a deep link hydrating) produce one URL patch
 * and one filters update, so there is no intermediate frame for another field to react to.
 */

/** Serialized form of a date bound when a range field is day-only. */
const DATE_VALUE_FORMAT = "YYYY-MM-DD";

/**
 * The route's query bag, as vue-router models it: a param is absent, a string, or repeated
 * into an array, and `null` for a valueless `?flag`.
 */
export type TRouteQuery = Record<string, string | null | (string | null)[] | undefined>;

/** Read one route query param as a single string (arrays collapse to their first entry). */
const readParam = (value: TRouteQuery[string]): string | null => {
  const raw = Array.isArray(value) ? value[0] : value;
  return typeof raw === "string" && raw !== "" ? raw : null;
};

/**
 * "No selection", covering both empties.
 *
 * A cleared Vuetify select emits `undefined`, not `null`. Testing only for `null` let that
 * through to `encode` / `String()`, which produced the literal string `"undefined"` in the URL
 * and in the request filters — a value the backend would filter on.
 */
const isEmptyValue = (value: TIncomingModel): value is null | undefined =>
  value === null || value === undefined;

/** Both ends of a date range, as the serialized strings the picker works in. */
export type TDateRangeModel = {
  start: string | null;
  end: string | null;
};

/**
 * The kinds of filter a field can be. This is the only thing a dashboard has to choose
 * between — everything else about a field (its URL params, its request keys, its empty
 * value, how it serializes) follows from the kind plus the API `field` name.
 */
export const EFilterType = {
  /** Single-select. Model holds the UI value; the API gets it under `field`. */
  SELECT: "select",
  /** Single-select whose API counterpart is list-valued — the picked id is wrapped in an array. */
  SELECT_LIST: "selectList",
  /** Two-ended date range — writes `<field>__gte` / `<field>__lte`. */
  DATE_RANGE: "dateRange",
} as const;

export type TFilterType = (typeof EFilterType)[keyof typeof EFilterType];

/** The UI value a select can hold — what a `v-model` on a Vuetify select produces. */
export type TSelectValue = string | number;

/**
 * The API value a field writes into the filters bag.
 *
 * Deliberately a closed union rather than `unknown`: a filter value is always a scalar, a
 * list of scalars (`SELECT_LIST`, and the expanded group keys), or an ISO bound. Anything
 * outside this is a declaration bug, and typing it precisely is what lets `writeFilter` and
 * the `encode` overrides be checked instead of cast.
 */
export type TFilterValue = TSelectValue | boolean | readonly TSelectValue[];

/** Every shape a field's model can take, across all three kinds. */
export type TFieldModel = TSelectValue | null | TDateRangeModel;

/**
 * The one `reactive` bag holding every field's model, keyed by declaration name.
 *
 * Heterogeneous by nature — a string select sits next to a date range — so the values are the
 * `TFieldModel` union here, and each key's precise type is recovered for callers by
 * `TFilterFields` when the bag is returned.
 */
type TModelState = Record<string, TFieldModel>;

/**
 * The request-filter bag the fields write into.
 *
 * Values are typed as what a field can actually put there, plus whatever the owning query
 * already holds (`page_size`, a tab's slice) — the fields only ever touch their own keys.
 */
type TFilterBag = Ref<Record<string, unknown>>;

/** Properties common to every field kind. */
type TFilterFieldBase = {
  /** API filter key. Omit for a URL-only field (no backend filter yet). */
  field?: string;
  /** URL param name (basename for ranges). Defaults to `field`. */
  queryKey?: string;
};

/**
 * A single-select field, in either of its two API flavours.
 *
 * `SELECT` sends the picked value as-is; `SELECT_LIST` wraps it in an array for a
 * list-valued backend filter. Both hold the same model, so they share one declaration type.
 */
export type TSelectFilterField<TValue extends TSelectValue = TSelectValue> = TFilterFieldBase & {
  type: typeof EFilterType.SELECT | typeof EFilterType.SELECT_LIST;
  /** Value the field starts at, and resets to. Defaults to no selection. */
  initial?: TValue | null;
  /**
   * URL string → UI value, for rejecting values the select cannot represent. Returning
   * `null` drops the param. Defaults to the raw string.
   */
  parse?: (raw: string) => TValue | null;
  /**
   * UI value → API value, for the cases the `type` cannot infer (a group key expanding into
   * its member types). `SELECT_LIST` already wraps in an array, so this is only for genuinely
   * custom mappings.
   */
  encode?: (value: TValue) => TFilterValue | undefined;
  /**
   * API value → UI value, used to seed the model from a filter value that is already in the
   * bag (an injected filter, a tab slice) so the select shows what the request filters by.
   * Only needed alongside a custom `encode`.
   */
  decode?: (value: TFilterValue) => TValue | null;
};

/** A two-ended date range, writing `<field>__gte` / `<field>__lte`. */
export type TDateRangeFilterField = TFilterFieldBase & {
  type: typeof EFilterType.DATE_RANGE;
  /** Range the field starts at, and resets to. Partial ends are filled in with `null`. */
  initial?: Partial<TDateRangeModel>;
  /** Preserve time-of-day through the model, the URL, and the bounds. */
  useTime?: boolean;
  /**
   * Both bounds → a single value under `field`, for backends that take the range as one
   * parameter rather than a `__gte` / `__lte` pair. Receives the ISO-widened bounds, so it
   * only has to join them; returning `undefined` (as it must for a half-open range the
   * parameter cannot express) clears the key.
   */
  encode?: (bounds: { start?: string; end?: string }) => TFilterValue | undefined;
};

/**
 * One filter field, declared as data.
 *
 * A discriminated union on `type`, so the internals narrow on it rather than casting, and a
 * declaration cannot mix range-only options (`useTime`) with select-only ones (`encode`).
 *
 * `field` is the API filter key *and* — unless `queryKey` overrides it — the URL param name.
 * Omit `field` for a filter whose backend counterpart does not exist yet: the field then
 * still syncs to the URL (so a reload and a shared link keep the selection) but writes
 * nothing into the request. Give it a `field` later and it starts filtering with no other
 * change.
 */
export type TFilterField<TValue extends TSelectValue = TSelectValue> =
  | TSelectFilterField<TValue>
  | TDateRangeFilterField;

/** A named set of field declarations — the shape `params.fields` takes. */
export type TFilterFieldMap = Record<string, TFilterField>;

/**
 * Extracts the model type from a declaration, so `query.f` stays fully typed.
 *
 * A range resolves to `TDateRangeModel`; a select resolves to its own value type, recovered
 * from `initial` / `parse` when the declaration pins one and falling back to the general
 * select value otherwise.
 */
export type TFilterFieldModel<TField> = TField extends TDateRangeFilterField
  ? TDateRangeModel
  : TField extends TSelectFilterField<infer TValue>
    ? TValue | null
    : never;

/** The `f` bag: one writable model per declared field. */
export type TFilterFields<TFields extends TFilterFieldMap> = {
  [K in keyof TFields]: TFilterFieldModel<TFields[K]>;
};

/** Convenience alias for a field bag exposed on a query result. */
export type TQueryFilterFields<TFields extends TFilterFieldMap> =
  | ComputedRef<TFilterFields<TFields>>
  | TFilterFields<TFields>;

/**
 * A type predicate, so every range branch below narrows to `TDateRangeFilterField` and can
 * read `useTime` — and every select branch narrows to `TSelectFilterField` and can read
 * `encode` / `parse` — without a cast.
 */
const isRange = (field: TFilterField): field is TDateRangeFilterField =>
  field.type === EFilterType.DATE_RANGE;

/**
 * What a `v-model` may hand back, before normalization.
 *
 * Wider than `TFieldModel` because a cleared Vuetify input emits `undefined`, and a picker
 * mid-edit can emit a range with `undefined` ends. Both are canonicalized by `normalize`.
 */
export type TIncomingModel = TSelectValue | null | undefined | Partial<TDateRangeModel>;

/** The value a field starts at, and returns to on `reset`. */
const initialOf = (field: TFilterField): TFieldModel => {
  if (isRange(field)) {
    // A partial `initial` range still has to produce both ends.
    return { start: field.initial?.start ?? null, end: field.initial?.end ?? null };
  }

  return field.initial ?? null;
};

/**
 * Narrow a model to the shape its field's kind guarantees.
 *
 * The models live in one heterogeneous bag, so a value arrives typed as the whole `TFieldModel`
 * union while the field has already narrowed to one kind. These recover the pairing by
 * *checking* rather than asserting: a mismatch (a range key holding a scalar, say) falls back
 * to that kind's empty instead of reaching `dayjs` or `String()` as a wrong-shaped value.
 */
const asRange = (value: TFieldModel): TDateRangeModel =>
  typeof value === "object" && value !== null ? value : { start: null, end: null };

const asSelect = (value: TFieldModel): TSelectValue | null =>
  typeof value === "object" && value !== null ? null : value;

/** Normalize whatever a `v-model` wrote into the canonical shape for the field's type. */
const normalize = (field: TFilterField, value: TIncomingModel): TFieldModel => {
  if (!isRange(field)) {
    return isEmptyValue(value) || typeof value === "object" ? null : value;
  }

  // A cleared picker can null the whole object, not just its ends.
  const range = typeof value === "object" && value !== null ? value : {};
  return { start: range.start ?? null, end: range.end ?? null };
};

/* ------------------------------------------------------------------ URL params */

/**
 * Keys a field owns, as a tuple so both ends of a range are statically present: a scalar
 * field owns one, a range owns its `_after` / `_before` (or `__gte` / `__lte`) pair.
 */
export type TKeys = { single: string; start: string; end: string };

/** URL param names a field owns: `single` for scalars, `start` / `end` for ranges. */
const paramKeys = (name: string, field: TFilterField): TKeys => {
  const key = field.queryKey ?? field.field ?? name;
  return { single: key, start: `${key}_after`, end: `${key}_before` };
};

/** Accept ISO or a bare date; normalize so a URL round-trip does not drift. */
const parseDate = (raw: string | null, useTime: boolean): string | null => {
  if (raw === null) return null;

  if (useTime) {
    const parsed = dayjs(raw);
    return parsed.isValid() ? parsed.toISOString() : null;
  }

  const parsed = dayjs(raw, DATE_VALUE_FORMAT, true);
  return parsed.isValid() ? parsed.format(DATE_VALUE_FORMAT) : null;
};

const serializeDate = (value: string | null, useTime: boolean): string | null => {
  if (!value) return null;
  if (!useTime) return value;

  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.toISOString() : null;
};

/**
 * Read one field's value out of the route query.
 *
 * A partial range is dropped rather than half-applied: one bound alone is not a selection the
 * picker can represent, and writing it back is what used to erase deep links a param at a time.
 */
const readField = (field: TFilterField, keys: TKeys, query: TRouteQuery): TFieldModel => {
  if (isRange(field)) {
    const useTime = field.useTime ?? false;
    const start = parseDate(readParam(query[keys.start]), useTime);
    const end = parseDate(readParam(query[keys.end]), useTime);
    const complete = Boolean(start && end);

    return { start: complete ? start : null, end: complete ? end : null };
  }

  const raw = readParam(query[keys.single]);
  if (raw === null) return null;

  return field.parse ? field.parse(raw) : raw;
};

/** One field's contribution to the URL patch. `null` deletes the param. */
const writeField = (
  field: TFilterField,
  keys: TKeys,
  value: TFieldModel,
  patch: Record<string, string | null>
) => {
  if (isRange(field)) {
    const range = asRange(value);
    const useTime = field.useTime ?? false;

    patch[keys.start] = serializeDate(range.start, useTime);
    patch[keys.end] = serializeDate(range.end, useTime);
    return;
  }

  const selected = asSelect(value);
  patch[keys.single] = selected === null ? null : String(selected);
};

/* -------------------------------------------------------------- request filters */

/**
 * API filter keys a field owns — used to clear them when the field empties or resets.
 *
 * `null` for a field with no `field`: URL-synced only, writes nothing into the request.
 */
const filterKeys = (field: TFilterField): TKeys | null => {
  if (!field.field) return null;
  return { single: field.field, start: `${field.field}__gte`, end: `${field.field}__lte` };
};

/**
 * One inclusive ISO bound. Without `useTime` the ends are widened to their whole day — the
 * serialized value carries no time, so an `lte` against bare midnight would match only that
 * instant and a same-day range would come back empty.
 */
const toIsoBound = (
  value: string | null,
  edge: "start" | "end",
  useTime: boolean
): string | undefined => {
  if (!value) return undefined;
  if (useTime) return dayjs(value).toISOString();

  return edge === "start"
    ? dayjs(value).startOf("day").toISOString()
    : dayjs(value).endOf("day").toISOString();
};

/**
 * Write a value into the filters bag, deleting the key when the value is empty.
 *
 * Keys are deleted rather than set to `undefined` so `filtersResetKey` (a `JSON.stringify`
 * of the bag) and the query key stay stable — an explicit `undefined` serializes away
 * anyway, but a deleted key also keeps the request payload clean.
 */
const writeFilter = (filters: TFilterBag, key: string, value: TFilterValue | undefined) => {
  if (value === undefined || value === null || (Array.isArray(value) && value.length === 0)) {
    delete filters.value[key];
  } else {
    filters.value[key] = value;
  }
};

/** UI value → API value, per the field's type. */
const encodeValue = (
  field: TSelectFilterField,
  value: TSelectValue | null
): TFilterValue | undefined => {
  if (value === null || value === undefined) return undefined;
  if (field.encode) return field.encode(value);

  return field.type === EFilterType.SELECT_LIST ? [value] : value;
};

/** API value → UI value, for seeding a select from a filter that is already in the bag. */
const decodeValue = (
  field: TSelectFilterField,
  value: TFilterValue | undefined
): TSelectValue | null | undefined => {
  if (value === undefined) return undefined;
  if (field.decode) return field.decode(value);

  if (field.type === EFilterType.SELECT_LIST) {
    return Array.isArray(value) ? (value[0] ?? null) : null;
  }

  // A list-valued filter cannot seed a plain `SELECT`, whose model holds a scalar.
  return Array.isArray(value) ? null : (value as TSelectValue);
};

/** Push one field's value into the request filters. */
const applyFilters = (
  field: TFilterField,
  keys: TKeys | null,
  value: TFieldModel,
  filters: TFilterBag
) => {
  if (!keys) return;

  if (isRange(field)) {
    const range = asRange(value);
    const useTime = field.useTime ?? false;
    const start = toIsoBound(range.start, "start", useTime);
    const end = toIsoBound(range.end, "end", useTime);

    // A single-parameter range owns one key, so the pair keys are never written for it.
    if (field.encode) {
      writeFilter(filters, keys.single, field.encode({ start, end }));
      return;
    }

    writeFilter(filters, keys.start, start);
    writeFilter(filters, keys.end, end);
    return;
  }

  writeFilter(filters, keys.single, encodeValue(field, asSelect(value)));
};

/* --------------------------------------------------------------- route writing */

/**
 * Pending same-tick patches, keyed by router instance.
 *
 * Two `router.replace` calls issued in one tick both read `currentRoute` *before* either
 * commits, so the second silently overwrites the first — and neither rejects, so there is no
 * error to retry on. One `useFilterFields` already batches its own fields, but a page can
 * hold more than one query, so patches are still merged into one navigation per tick.
 *
 * A `WeakMap` keeps this per-router so parallel tests — and any app with more than one
 * router instance — do not share a batch.
 */
const pendingPatches = new WeakMap<
  Router,
  { patch: Record<string, string | null>; promise: Promise<void> }
>();

/**
 * Route-query writer shared by every field set — and by any other component that writes
 * route-query params directly (search box, tab strip), so their writes join the same batch
 * instead of racing a stale `route.query` snapshot against it.
 *
 * A `null` entry deletes its key. Navigation aborts are swallowed: a competing
 * `router.replace` is normal here, and the next change re-asserts.
 */
export const useQueryWriter = () => {
  const router = useRouter();

  return (patch: Record<string, string | null>) => {
    const existing = pendingPatches.get(router);

    // Join the batch already scheduled for this tick.
    if (existing) {
      Object.assign(existing.patch, patch);
      return existing.promise;
    }

    const batch = { ...patch };
    const promise = Promise.resolve().then(async () => {
      pendingPatches.delete(router);

      const query = { ...router.currentRoute.value.query };
      let changed = false;

      for (const [key, value] of Object.entries(batch)) {
        const current = readParam(query[key]);
        if (current === value) continue;

        if (value === null) delete query[key];
        else query[key] = value;

        changed = true;
      }

      if (!changed) return;

      try {
        await router.replace({ query });
      } catch {
        // Aborted by a competing navigation — the next change re-asserts.
      }
    });

    pendingPatches.set(router, { patch: batch, promise });

    return promise;
  };
};

/* ----------------------------------------------------------------------- tabs */

/**
 * Tab-driven filters, previously a `watch(activeTab)` copy-pasted into every dashboard page.
 *
 * The active tab contributes a slice of request filters; switching tabs must remove the
 * previous tab's keys before merging the new ones, or a narrowing filter from one tab leaks
 * into the next. Runs `sync` and immediate so the first tab resolution lands before the
 * query's `enabled` gate opens.
 */
export type TTabSource<TFilters> = MaybeRefOrGetter<{ filters?: Partial<TFilters> } | null>;

export const useTabFilters = <TFilters extends object>(
  filters: Ref<Partial<TFilters>>,
  tab: TTabSource<TFilters>
) => {
  watch(
    () => toValue(tab),
    (next, previous) => {
      if (previous?.filters) {
        for (const key of Object.keys(previous.filters)) {
          delete (filters.value as Record<string, unknown>)[key];
        }
      }
      if (next?.filters) {
        Object.assign(filters.value, next.filters);
      }
    },
    { immediate: true, flush: "sync" }
  );
};

/* ------------------------------------------------------------------- the wiring */

/**
 * Wires a set of field declarations against an owned filters ref.
 *
 * Every model lives in one `reactive` bag with one watcher over it, so a change to any field
 * — or to several at once — produces a single URL patch and a single pass over the request
 * filters. Returns the bag plus a `reset` that returns every field to its declared `initial`.
 */
export const useFilterFields = <TFilters extends object, TFields extends TFilterFieldMap>(
  filters: Ref<Partial<TFilters>>,
  fields: TFields
): {
  f: TFilterFields<TFields>;
  reset: () => void;
} => {
  const bag = filters as Ref<Record<string, unknown>>;
  const route = useRoute();
  const writeQuery = useQueryWriter();

  const entries = Object.entries(fields) as [string, TFilterField][];

  // Params and filter keys are static per declaration — resolve them once.
  const params = new Map(entries.map(([name, field]) => [name, paramKeys(name, field)]));
  const apiKeys = new Map(entries.map(([name, field]) => [name, filterKeys(field)]));

  /**
   * Seed from `initial`, then from any filter value already in the bag (an injected filter,
   * a tab slice) so a select shows what the request is actually filtering by. The URL wins
   * over both, and is applied by the hydrating watcher below.
   */
  const seed = (): TModelState => {
    const state: TModelState = {};

    for (const [name, field] of entries) {
      const keys = apiKeys.get(name) ?? null;
      const existing =
        isRange(field) || !keys
          ? undefined
          : decodeValue(field, bag.value[keys.single] as TFilterValue | undefined);

      state[name] = existing === undefined ? initialOf(field) : existing;
    }

    return state;
  };

  const f: TModelState = reactive(seed());

  /**
   * URL → models, immediate so a deep link wins over the seeded state.
   *
   * On the first run an absent param must not clear a seeded or `initial` value; later, an
   * absent param means the user cleared the field (or navigated back) and does apply.
   */
  let hydrated = false;

  watch(
    () => route.query,
    (query) => {
      for (const [name, field] of entries) {
        const next = readField(field, params.get(name)!, query);
        // Every key is seeded, so this only defaults on a name that was never declared.
        const current = f[name] ?? null;

        if (!hydrated && isEmptyOf(field, next) && !isEmptyOf(field, current)) continue;
        if (isSame(field, current, next)) continue;

        f[name] = next;
      }

      hydrated = true;
    },
    { immediate: true, flush: "sync" }
  );

  /**
   * Models → URL + request filters, in one pass. Both writes share a watcher so they can
   * never disagree, and the whole bag is one patch so no field clobbers another.
   */
  watch(
    f,
    () => {
      const patch: Record<string, string | null> = {};

      for (const [name, field] of entries) {
        // A `v-model` can write `undefined` or a half-built range; canonicalize before it
        // reaches `encode` / `String()`, which would otherwise stringify it.
        const value = normalize(field, f[name] ?? null);

        applyFilters(field, apiKeys.get(name) ?? null, value, bag);
        writeField(field, params.get(name)!, value, patch);
      }

      void writeQuery(patch);
    },
    { immediate: true, flush: "sync", deep: true }
  );

  const reset = () => {
    for (const [name, field] of entries) {
      f[name] = initialOf(field);
    }
  };

  return { f: f as TFilterFields<TFields>, reset };
};

/** Is this value the field's "nothing selected"? Range emptiness is both ends null. */
const isEmptyOf = (field: TFilterField, value: TFieldModel): boolean => {
  if (!isRange(field)) return isEmptyValue(asSelect(value));

  const range = asRange(value);
  return isEmptyValue(range.start) && isEmptyValue(range.end);
};

/** Value equality, so an unchanged URL read does not churn the model. */
const isSame = (field: TFilterField, a: TFieldModel, b: TFieldModel): boolean => {
  if (!isRange(field)) return Object.is(a, b);

  const left = asRange(a);
  const right = asRange(b);

  return left.start === right.start && left.end === right.end;
};

/**
 * The module's pure helpers, exposed as one namespace for unit tests.
 *
 * They are implementation detail — application code should use `useFilterFields` (the default
 * export) and never reach in here. Grouping them behind one name keeps that boundary obvious
 * at the import site and stops the helpers leaking into the barrel's public surface.
 */
export const filterFieldInternals = {
  readParam,
  isEmptyValue,
  isRange,
  initialOf,
  asRange,
  asSelect,
  normalize,
  paramKeys,
  parseDate,
  serializeDate,
  readField,
  writeField,
  filterKeys,
  toIsoBound,
  writeFilter,
  encodeValue,
  decodeValue,
  applyFilters,
  isEmptyOf,
  isSame,
};

export default useFilterFields;
